import {
  ColumnDef,
  FilterFn,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import { TableComponent } from 'components/table/TableComponent'
import PDFPreviewer from 'features/assetDatabase/components/assetOverview/assetDetails/PDFPreviewer'
import { usePdfPreview } from 'features/assetDatabase/components/assetOverview/assetFiles/fileTableUtils'
import { useRolodexEntries } from 'features/rolodex'
import React, { useState } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  GeckoComponent,
  GeckoFileCategory,
  GeckoReport,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

import { defaultFilesTableColumns } from './FilesTableColumns'

export type FilesTableEntry = {
  id: string
  visibleName: string
  uploadDate: string
  componentIds: string[]
  componentNames: string[]
  unitIds: string[]
  fileCategory: GeckoFileCategory
  fileType: string
  inspectionIds: string[]
  jobIds: string[]
}

const PageContainer = styled.div`
  margin-top: 12px;
  width: 100%;
  overflow: auto;
  margin-bottom: 1rem;
`

export type BasicComponent = {
  name: string
  id: string
}

interface FilesTableProps {
  unitID: string
  reports: readonly GeckoReport[]
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
  customColumns?: ColumnDef<FilesTableEntry>[]
  showColumnConfig?: boolean
  showFilterBar?: boolean
  components: readonly GeckoComponent[]
  unit?: GeckoUnit
  refreshData?: () => Promise<void>
  refreshThumbnails?: () => Promise<void>
  setIsPrimaryImageLoaded?: (loaded: boolean) => void
}

export const FilesTable: React.FC<FilesTableProps> = ({
  unitID,
  reports,
  isLoading,
  hasEverLoaded,
  customColumns,
  forceRefresh,
  showColumnConfig = true,
  showFilterBar = true,
  components,
  unit,
  refreshData,
  refreshThumbnails,
  setIsPrimaryImageLoaded,
}) => {
  const { theme } = useGeckoTheme()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 24,
  })
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'uploadDate', desc: true },
  ])
  const [selectedRow, setSelectedRow] = useState<FilesTableEntry | null>(null)
  const [showPdfPreviewer, setShowPdfPreviewer] = useState(false)

  const { pdfURL, isLoading: isPDFLoading } = usePdfPreview(
    selectedRow?.id || '',
    selectedRow?.fileType || '',
    setShowPdfPreviewer
  )

  const { components: allComponents } = useRolodexEntries({
    type: RolodexEntryType.COMPONENT,
    ids: reports
      .map((report) => report.data.components)
      .flat()
      .filter((component) => component !== undefined),
  })

  const data: FilesTableEntry[] = reports
    .filter((report) => report.data.file_category !== GeckoFileCategory.IMAGE) // Images are handled purely on the details tab now
    .map((report) => {
      const componentNames = (report.data.components ?? [])
        .map((componentId) => {
          const component = allComponents.find(
            (comp) => comp.id === componentId
          )
          return component ? component.data.name : null
        })
        .filter((name) => name !== null)
      return {
        id: report.id as string,
        visibleName: report.data.visibleName,
        uploadDate:
          report.data.upload_date instanceof Date &&
          !isNaN(report.data.upload_date.getTime())
            ? report.data.upload_date.toISOString()
            : '',
        componentIds: [...(report.data.components ?? [])],
        componentNames: componentNames,
        unitIds: [...(report.data.units ?? [])],
        inspectionIds: [...(report.data.inspections ?? [])],
        jobIds: [...(report.data.jobs ?? [])],
        fileCategory: report.data.file_category ?? GeckoFileCategory.OTHER,
        fileType: report.data.fileType,
      }
    })

  const columns: ColumnDef<FilesTableEntry>[] = customColumns
    ? customColumns
    : defaultFilesTableColumns(
        unitID,
        forceRefresh,
        components.map((component) => ({
          name: component.data.name,
          id: component.id,
        })),
        theme.forecast.defaultButton,
        unit,
        refreshData,
        refreshThumbnails,
        setIsPrimaryImageLoaded
      )

  const globalFilterFn: FilterFn<FilesTableEntry> = (row, _, filterValue) => {
    return row.original.visibleName?.toLowerCase().includes(filterValue)
  }

  const handleRowClick = (row: FilesTableEntry) => {
    setSelectedRow(row)
  }

  return (
    <PageContainer>
      <TableComponent
        isLoadingData={isLoading || !hasEverLoaded}
        data={data}
        columns={columns}
        footerName='File(s)'
        pagination={pagination}
        globalFilterFn={globalFilterFn}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={(row) => handleRowClick(row.original)}
        showColumnConfig={showColumnConfig}
        showFilterBar={showFilterBar}
      />
      {showPdfPreviewer && (
        <PDFPreviewer
          pdfURL={pdfURL}
          setIsPdfPreviewOpen={() => {
            setSelectedRow(null)
            setShowPdfPreviewer(false)
          }}
          isLoading={isPDFLoading}
          fileType={selectedRow?.fileType || ''}
        />
      )}
    </PageContainer>
  )
}
